import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body2,
    FilledCard,
    Heading2,
} from '@myphx-engineering/phx-design-system';

import type {
    AuthorizedMedicationInfoFragment,
    CashOfferInfoFragment,
    InsuranceOfferInfoFragment,
    NonPriceableInsuranceOfferInfoFragment,
    NonPriceableOfferInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../../graphql/generated/graphql';
import type { HeadlinePrice } from '../../../../hooks/use-config-api';
import { useProgramTerms } from '../../../../hooks/use-program-terms';

import { InsuranceSavingsCard } from './InsuranceSavingsCard';
import { SelfPaySavingsCard } from './SelfPaySavingsCard';

export type OrderSavingsCardProps = {
    medication:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
    selectedOffer:
        | CashOfferInfoFragment
        | InsuranceOfferInfoFragment
        | NonPriceableInsuranceOfferInfoFragment
        | NonPriceableOfferInfoFragment
        | undefined;
};

export const OrderSavingsCard: FC<OrderSavingsCardProps> = ({
    medication,
    selectedOffer,
}) => {
    const { t } = useTranslation('lilly');

    const insuranceTerms = useProgramTerms(medication, 'insurance');
    const selfPayTerms = useProgramTerms(medication, 'self-pay');

    if (
        selectedOffer?.__typename !== 'CashOffer' &&
        selectedOffer?.__typename !== 'InsuranceOffer'
    ) {
        return null;
    }

    const { headlinePriceType, coverageCard } = selectedOffer;
    const priceType = headlinePriceType
        ? (headlinePriceType as HeadlinePrice['type'])
        : null;

    // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
    const SavingsCardComponent = useMemo(() => {
        if (priceType === 'CopayAssistance' && !!insuranceTerms?.savingsCard) {
            return InsuranceSavingsCard;
        }

        if (
            (priceType === 'CashAssistance' || priceType === 'Incentive') &&
            !!selfPayTerms?.savingsCard
        ) {
            return SelfPaySavingsCard;
        }

        return null;
    }, [insuranceTerms, priceType, selfPayTerms]);

    if (!SavingsCardComponent || !coverageCard) {
        return null;
    }

    return (
        <>
            <FilledCard
                withBorder
                color="primary"
                data-testid="order-confirmation-savings-card"
                header={
                    <Heading2>
                        {t('orderConfirmation.savingsCard.header')}
                    </Heading2>
                }
            >
                <SavingsCardComponent
                    coverageCard={coverageCard}
                    medication={medication}
                />
            </FilledCard>
            {priceType === 'CopayAssistance' && insuranceTerms?.savingsCard ? (
                <Body2>
                    {t('savingsCard.disclaimer', {
                        programName: insuranceTerms.savingsCard.name,
                    })}
                </Body2>
            ) : null}
        </>
    );
};
